import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING, LIST, ADD, EDIT, ASC,
} from "const"
import { MtTask } from "server/models"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_TASK,
    StoreType,
    TaskList,
    TaskFilters,
    TasksOnSelect,
    AccountUserList,
    ProjectUserList,

    LoadAllProjects,
    LoadProjectUsers,
    LoadProjectUserGroups,
    LoadProjectPermissions,
    LoadAllTasks,
    LoadAllTaskParticipants,
    LoadAllTaskObservers,
    LoadAccountUsers,
    SortTasks,
    GroupTasksByParent,
    GetTaskNested,
    PrepareTaskNestedIds,
    ClearParentsTask,
    DeleteTask,

    setProjectList,
    setSystemProjectUserGroups,
    setSystemProjectPermissions,
    setProjectUserList,
    setTasksOnSelect,
    setTaskSelected,
    setTaskList,
    setTaskGroupedIds,
    setTaskSort,
    setTaskFilters,
    setTaskParticipantList,
    setTaskObserverList,
    setAccountUserList,
    addModal,
    closeModal,
} from "client/store"
import {
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Loader,
    Button,
    Table,
    TableSearchWrapper,
    TableGroupedIds,
    SortRows,
    DeleteRow,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import TaskSearch from "client/pages/search/taskSearch"
import Columns from "./columns"
import PopupMenu from "./popupMenu"
import DisplayTaskColumns from "./displayColumns"

import { TaskListWrapper } from "./styled"

const subtaskFilters: TaskFilters = {
    status: {
        pending: true,
        inProgress: true,
        pendingReview: true,
        completed: true,
        deferred: true,
    },
}

interface TaskListProps {
    parentTaskId?: number
    onSelectTasks?: TasksOnSelect
}

const List = ({
    parentTaskId = 0, // 0 - main task list
    onSelectTasks = null,
}: TaskListProps) => {
    const baseRoute = useSelector((state: StoreType) => state.task.baseRoute)
    const selected = useSelector((state: StoreType) => state.task.selected)

    const taskList = useSelector((state: StoreType) => state.task.taskList)
    const taskGroupedIds = useSelector((state: StoreType) => state.task.taskGroupedIds)
    const taskSort = useSelector((state: StoreType) => state.task.taskSort)
    const filters = useSelector((state: StoreType) => state.task.filters)

    const { sortColumn, sortOrder } = taskSort[parentTaskId] || {}

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        dispatch(setTasksOnSelect(onSelectTasks))
        dispatch(setTaskSelected(null))

        dispatch(setTaskSort({
            ...taskSort,
            [parentTaskId]: {
                sortColumn: "name",
                sortOrder: ASC,
            },
        }))

        const promiseArray: Promise<any>[] = [
            LoadAllTasks(userData.accountId, project.id, userData.userId).then((res) => {
                dispatch(setTaskList(res))
            }),
            LoadAllTaskParticipants().then((res) => {
                dispatch(setTaskParticipantList(res))
            }),
            LoadAllTaskObservers().then((res) => {
                dispatch(setTaskObserverList(res))
            }),
        ]

        if (!project.id && parentTaskId === 0) {
            promiseArray.push(
                // project list for search
                LoadAllProjects(userData.accountId).then((res) => {
                    dispatch(setProjectList(res))

                    const promiseNestedArray: Promise<any>[] = []
                    Object.values(res).forEach((projectRow) => {
                        promiseNestedArray.push(
                            // to display user status for project level tasks
                            LoadProjectUsers(projectRow.id).then((resUsers) => {
                                const newProjectUserList: ProjectUserList = { [projectRow.id]: resUsers }
                                dispatch(setProjectUserList(newProjectUserList))
                            }),
                            // to check user access for project level tasks
                            LoadProjectUserGroups(userData.userId, userData.accountId, projectRow.id)
                            .then((projectGroupIds) => {
                                dispatch(setSystemProjectUserGroups(projectRow.id, projectGroupIds))
                            }),
                            LoadProjectPermissions(userData.accountId, projectRow.id)
                            .then((permissions) => {
                                dispatch(setSystemProjectPermissions(projectRow.id, permissions.permissions))
                            }),
                        )
                    })
                    return Promise.all(promiseNestedArray)
                }),
                // account user list for search
                LoadAccountUsers(userData.accountId).then((res) => {
                    const accountUserList: AccountUserList = { [userData.accountId]: res }
                    dispatch(setAccountUserList(accountUserList))
                }),
            )
        }

        setLoadState(PENDING)
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
        })
    }, [])

    useEffect(() => {
        if (parentTaskId !== 0) { // sorting subtasks
            const tasksFiltered = SortTasks(Object.values(taskList), sortColumn, sortOrder, subtaskFilters)
            const groupedByParent = GroupTasksByParent(tasksFiltered)
            const nestedIds = GetTaskNested(parentTaskId, groupedByParent)
            if (nestedIds.length) {
                const nestedGroupedIds: TableGroupedIds = {
                    groupId: null, // no grouping
                    nestedIds,
                    nestedGroupedIds: [],
                }
                dispatch(setTaskGroupedIds({ [parentTaskId]: [nestedGroupedIds] }))
            } else {
                dispatch(setTaskGroupedIds({ [parentTaskId]: [] }))
            }
        } else {
            const tasksFiltered = SortTasks(Object.values(taskList), sortColumn, sortOrder, filters)
            const groupedByProject = PrepareTaskNestedIds(tasksFiltered, !project.id)
            dispatch(setTaskGroupedIds({ [parentTaskId]: Object.values(groupedByProject) }))
        }
    }, [taskList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtTask, order) => {
        dispatch(setTaskSort({
            [parentTaskId]: {
                sortColumn: column,
                sortOrder: order,
            },
        }))
    }

    const onFilter = (newFilters: TaskFilters) => {
        dispatch(setTaskFilters(newFilters))
    }

    const onDelete: DeleteRow = (task: MtTask, callback) => {
        // clear parents
        ClearParentsTask(task.id, userData.accountId).then(() => {
            DeleteTask(task.id, userData.accountId).then((removedRows) => {
                const success = removedRows === 1
                callback(success, () => {
                    const taskListCopy: TaskList = taskList ? deepCopy(taskList) : {}
                    delete taskListCopy[task.id]
                    dispatch(setTaskList(taskListCopy))
                })
            })
        })
    }

    const onSelect = () => {
        const selectedTasks: MtTask[] = []
        if (selected) {
            selected.forEach((id) => {
                selectedTasks.push(taskList[id])
            })
        }
        onSelectTasks(selectedTasks)
        dispatch(closeModal()) // close task list modal
    }

    const { columns, displayColumns } = Columns()
    const dataGroupedIds = taskGroupedIds[parentTaskId] || []

    const listAccess = !project.id
        ? hasAccess(PRM_TASK, LIST)
        : hasProjectAccess(PRM_TASK, LIST)

    const addAccess = !project.id
        ? hasAccess(PRM_TASK, ADD)
        : hasProjectAccess(PRM_TASK, ADD)

    if (!listAccess) return <AccessDenied />

    // don't show subtasks if the list is empty
    if (parentTaskId !== 0 && dataGroupedIds.length === 0) return null
    return (
        <TaskListWrapper>
            {
                parentTaskId === 0 && (
                    <PageHeader>
                        <PageTitle>{lang.tasks}</PageTitle>
                    </PageHeader>
                )
            }
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                {
                    parentTaskId === 0 && (
                        <TableSearchWrapper>
                            <TaskSearch
                                id="taskList"
                                rightOffset={addAccess && !onSelectTasks}
                                filters={filters}
                                onFilter={onFilter}
                            />
                            {
                                addAccess && !onSelectTasks && (
                                    <Button
                                        variant="success"
                                        onClick={() => dispatch(addModal({ url: `${baseRoute}/${EDIT}/0` }))}
                                    >
                                        {lang.add}
                                    </Button>
                                )
                            }
                        </TableSearchWrapper>
                    )
                }
                <Table
                    title={parentTaskId !== 0 && lang.subTasks}
                    columns={columns}
                    dataList={taskList}
                    dataGroupedIds={dataGroupedIds}
                    selectedIds={selected}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="fit"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />

                <PopupMenu />
                <DisplayTaskColumns
                    columns={columns.filter((column) => !!column.header)} // don't show management columns
                    displayColumns={displayColumns}
                />

                {
                    onSelectTasks && (
                        <PageForm>
                            <Button variant="success" onClick={onSelect}>{lang.select}</Button>
                        </PageForm>
                    )
                }
            </PageContent>
        </TaskListWrapper>
    )
}
export default List
